'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import KPIBentoGrid from './components/KPIBentoGrid';
import ChartsRow from './components/ChartsRow';
import RiskEventTable from './components/RiskEventTable';
import AlertFeed from './components/AlertFeed';
import AIInsightsSidebar from './components/AIInsightsSidebar';
import { useAuth } from '@/lib/AuthContext';
import { getRiskEvents, saveRiskEvents } from '@/lib/storage';
import { DEFAULT_RISK_EVENTS, RiskEvent } from '@/lib/riskData';
import { Plus, RefreshCw, Building2, AlertTriangle, TrendingUp } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


export default function AiRiskDashboardPage() {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState<RiskEvent[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const loadEvents = useCallback(() => {
    const stored = getRiskEvents();
    if (stored.length === 0) {
      saveRiskEvents(DEFAULT_RISK_EVENTS);
      setEvents(DEFAULT_RISK_EVENTS);
    } else {
      setEvents(stored);
    }
    setLastRefreshed(new Date());
  }, []);

  useEffect(() => {
    loadEvents();
    const interval = setInterval(() => {
      loadEvents();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadEvents]);

  const handleRefresh = () => {
    loadEvents();
  };

  const handleEventsChange = (updated: RiskEvent[]) => {
    setEvents(updated);
    saveRiskEvents(updated);
  };

  const criticalCount = events.filter(e => e.severity === 'Critical').length;
  const escalatingCount = events.filter(e => e.status === 'Escalating').length;

  return (
    <AppLayout activeRoute="/ai-risk-dashboard" onRefresh={handleRefresh} lastRefreshed={lastRefreshed}>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Risk Intelligence Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              AI-classified threat feed · <span className="text-danger font-medium">{criticalCount} critical</span> · <span className="text-warning font-medium">{escalatingCount} escalating</span>
              <span className="ml-2 text-muted-foreground/60">· auto-refresh every 30s</span>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-md px-3 py-2 hover:bg-muted transition-colors"
            >
              <RefreshCw size={13} />
              <span>Refresh</span>
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 text-sm bg-primary/10 text-primary border border-primary/30 font-medium rounded-md px-3 py-2 hover:bg-primary/20 transition-colors"
              >
                <Plus size={13} />
                Add Event
              </button>
            )}
            <Link
              href="/ai-reports"
              className="flex items-center gap-2 text-sm bg-primary text-primary-foreground font-medium rounded-md px-4 py-2 hover:opacity-90 active:scale-95 transition-all duration-150"
            >
              Generate Report
            </Link>
          </div>
        </div>

        {/* Company Exposure Banner */}
        <div className="card-elevated rounded-xl border border-warning/20 bg-warning/5 p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-8 h-8 rounded-lg bg-warning/20 flex items-center justify-center flex-shrink-0">
              <Building2 size={15} className="text-warning" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <p className="text-sm font-semibold text-foreground">Acme Corp — Active Exposure Summary</p>
                <span className="text-2xs px-2 py-0.5 rounded bg-warning/20 text-warning font-semibold">3 HIGH-PRIORITY EXPOSURES</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                {[
                  { icon: AlertTriangle, text: '2 monitored suppliers exposed to new OFAC screening requirements', color: 'text-danger' },
                  { icon: TrendingUp, text: '1 Red Sea route affected — estimated delay +12 days, freight cost +22%', color: 'text-warning' },
                  { icon: AlertTriangle, text: 'Taiwan Strait escalation raises procurement risk for East Asia electronics sourcing', color: 'text-warning' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <Icon size={12} className={`${item.color} flex-shrink-0 mt-0.5`} />
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <Link href="/company-analyser" className="text-xs text-primary hover:text-primary/80 font-medium transition-colors flex-shrink-0">
              View full exposure →
            </Link>
          </div>
        </div>

        {/* KPI Bento */}
        <KPIBentoGrid />

        {/* Charts + AI Sidebar */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          <div className="xl:col-span-3 space-y-6">
            <ChartsRow />
          </div>
          <div className="xl:col-span-1">
            <AIInsightsSidebar />
          </div>
        </div>

        {/* Event Table + Alert Feed */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          <div className="xl:col-span-3">
            <RiskEventTable events={events} onEventsChange={handleEventsChange} isAdmin={isAdmin} />
          </div>
          <div className="xl:col-span-1">
            <AlertFeed />
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      {showAddModal && isAdmin && (
        <AddEventModal
          onClose={() => setShowAddModal(false)}
          onAdd={(event) => {
            const updated = [event, ...events];
            handleEventsChange(updated);
            setShowAddModal(false);
          }}
        />
      )}
    </AppLayout>
  );
}

interface AddEventModalProps {
  onClose: () => void;
  onAdd: (event: RiskEvent) => void;
}

function AddEventModal({ onClose, onAdd }: AddEventModalProps) {
  const [form, setForm] = useState({
    event: '',
    category: 'Sanctions' as RiskEvent['category'],
    severity: 'Medium' as RiskEvent['severity'],
    region: '',
    sector: '',
    confidence: 80,
    escalationProb: 40,
    status: 'Monitoring' as RiskEvent['status'],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.event.trim() || !form.region.trim()) return;
    const now = new Date();
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    onAdd({
      id: `evt-${Date.now()}`,
      ...form,
      updated: timeStr,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-card-lg w-full max-w-lg mx-4 p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Add Risk Event</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Event Title *</label>
            <input
              type="text"
              value={form.event}
              onChange={e => setForm(f => ({ ...f, event: e.target.value }))}
              placeholder="Describe the risk event…"
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value as RiskEvent['category'] }))}
                className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {['Sanctions', 'Cyber', 'Maritime', 'Supply Chain', 'Regional Instability', 'Energy'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Severity</label>
              <select
                value={form.severity}
                onChange={e => setForm(f => ({ ...f, severity: e.target.value as RiskEvent['severity'] }))}
                className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {['Critical', 'High', 'Medium', 'Low'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Region *</label>
              <input
                type="text"
                value={form.region}
                onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                placeholder="e.g. Middle East / Gulf"
                className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Sector</label>
              <input
                type="text"
                value={form.sector}
                onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}
                placeholder="e.g. Energy, Finance"
                className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Confidence: {form.confidence}%</label>
              <input type="range" min={0} max={100} value={form.confidence} onChange={e => setForm(f => ({ ...f, confidence: +e.target.value }))} className="w-full accent-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Escalation Prob: {form.escalationProb}%</label>
              <input type="range" min={0} max={100} value={form.escalationProb} onChange={e => setForm(f => ({ ...f, escalationProb: +e.target.value }))} className="w-full accent-primary" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Status</label>
            <select
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value as RiskEvent['status'] }))}
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {['Escalating', 'Critical', 'Monitoring', 'Resolved', 'Detected', 'Classified'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-border rounded-md py-2 text-sm text-muted-foreground hover:bg-muted transition-colors">
              Cancel
            </button>
            <button type="submit" className="flex-1 bg-primary text-primary-foreground rounded-md py-2 text-sm font-semibold hover:opacity-90 transition-all">
              Add Event
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}